import { observer } from "mobx-react-lite";
import { useState, useRef } from "react";
import pageBuilderStore from "../../stores/pageBuilderStore";
import {X, Upload, ImageUp} from "lucide-react";
import ContentSection from "../ui/sidebar/content-blocks";
import LayoutSection from "../ui/sidebar/layout";
import TypographySection from "../ui/sidebar/typography";
import ImageLibrary from "../ui/sidebar/image-library";
import ColumnsOptions from "../ui/sidebar/columns-options";
import ButtonOptions from "../ui/sidebar/button-options";
import blocksManager from "../../stores/blocks-manager";
import DividerOptions from "../ui/sidebar/divider-options";
import HeadingStyles from "../ui/sidebar/heading-styles";
import TextStyles from "../ui/sidebar/text-styles";
import ImageOptions from "../ui/sidebar/image-options";
import HeroSliderOptions from "../ui/sidebar/hero-slider-options";
import VideoOptions from "../ui/sidebar/video-options";
import CustomHtmlOptions from "../ui/sidebar/custom-html-options";
import SocialsOptions from "../ui/sidebar/socials-options";
import { IImageAsset } from "../ui/shared/draggable-asset";


/**
 * SidebarPanel – Main Properties & Controls Sidebar for PageBuilder
 *
 * @component
 * @description
 * Always-visible right sidebar that dynamically displays contextual editing panels
 * based on the user's current selection or editing context. Includes block/content selection,
 * global body/layout/typography settings, block property editors, and the image library.
 *
 * @features
 * - Panel header with dynamic title based on the current view (Content, Global Styles, Block Properties, Image Library)
 * - Multi-view switching: content blocks, body/global settings, image upload/library, and block property editors
 * - Inline image upload with drag-and-drop and button-select (shows filename and upload state)
 * - Fully managed by MobX store state (`pageBuilderStore` for panel view, block selection, etc.)
 * - Includes all block property panels (columns, button, divider, heading, text, image, hero slider, video, socials, custom HTML)
 * - Integrates ImageLibrary for browsing and inserting images
 *
 * @state
 * - Uses MobX for active panel, current block, and image library state
 * - Local React state for upload progress and file selection in the image upload section
 *
 * @remarks
 * - Designed for internal use in PageBuilder, not as a standalone sidebar or for reuse in other apps
 * - Always visible; no longer toggled/hidden, only its views change based on context
 * - Easily extended to add new panel types or editors
 *
 * @example
 * // Used as the persistent sidebar in the PageBuilder layout
 * <SidebarPanel />
 *
 * @since 2025-06-10
 * @lastUpdated 2025-07-15
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default observer(function SidebarPanel () {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image'));
    if (files.length > 0) {
      setSelectedFiles(files);
      uploadImages(files);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).filter(file => file.type.startsWith("image/"));
    if (files.length > 0) {
      setSelectedFiles(files);
      uploadImages(files);
    }
  }

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  async function uploadImages(files: File[]) {
    setIsUploading(true);
    const formData = new FormData();
    files.forEach(file => formData.append("images", file));
    try {
      /** TODO: Replace the /POST API url here with an actual one. */  
      const res = await fetch("/api/pagebuilder/image-library/upload", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (data.success && data.uploaded) {
        // Add each new image URL to the MobX image library here
        const imageAssets: IImageAsset[] = data.uploaded.map((item: any) => ({
          url: item.url,
          ...item
        }));

        pageBuilderStore.addToImageLibraryAssets(imageAssets);
      }
    } catch (error) {
      alert("Image upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      setSelectedFiles([]);
    }
  }

	return (
    <div className="flex flex-col h-[900px] w-full overflow-y-auto">
      {/* Panel Header */}
      <div
        h-14
        className="flex items-center justify-between p-4 border-b bg-gray-50"
      >
        <h3 className="font-semibold text-gray-800">
          {pageBuilderStore.activePanel === "content" && "Content"}
          {pageBuilderStore.activePanel === "body" && "Global Styles"}
          {pageBuilderStore.activePanel === "block-properties" &&
            "Block Properties"}
          {pageBuilderStore.activePanel === "images" && "Image Library"}
        </h3>
      </div>
      {/* Panel Content */}
      <div className="flex-1 h-full overflow-y-auto">
        {pageBuilderStore.activePanel === "content" && <ContentSection />}

        {pageBuilderStore.activePanel === "body" && (
          <div>
            {/* Layout Properties Section */}
            <div className="flex flex-col h-max w-full">
              <LayoutSection />

              <TypographySection />
            </div>
          </div>
        )}

        {pageBuilderStore.activePanel === "images" && (
          <div>
            <div className="mb-6 p-4">
              {/* Upload Dropzone */}
              <div
                className="flex flex-col items-center justify-center mb-4 w-full min-h-[130px] border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:border-blue-400 transition"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}
              >
                {selectedFiles.length > 0 ? (
                  <div className="flex flex-col gap-2 items-center w-full">
                    {selectedFiles.map(file => (
                      <div key={file.name} className="text-base text-gray-600">{file.name}</div>
                    ))}
                    {isUploading && <div className="text-blue-400">Uploading...</div>}
                  </div>
                ) : (
                  <>
                    <ImageUp size={38} className="text-blue-400 mb-2" />
                    <div className="text-base font-medium text-gray-600">Drop Image Here to Upload</div>
                  </>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  multiple
                  accept="image/*"
                  disabled={isUploading}
                />
              </div>
              <button
                className="flex w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium justify-center items-center"
                onClick={handleUploadClick}
                disabled={isUploading}
              >
                <Upload size={16} className="mr-2" />
                Upload an Image
              </button>

              <hr className="h-[2px] my-2" />

              <ImageLibrary />
            </div>
          </div>
        )}

        {pageBuilderStore.activePanel === "block-properties" &&
          blocksManager.getBlockEditing().selectedBlockId && (
            <div>
              {blocksManager.getBlockEditing().selectedBlockType ===
                "columns" && (
                <ColumnsOptions
                  blockId={blocksManager.getBlockEditing().selectedBlockId!}
                />
              )}
              {blocksManager.getBlockEditing().selectedBlockType ===
                "button" && (
                <ButtonOptions
                  blockId={blocksManager.getBlockEditing().selectedBlockId!}
                />
              )}
              {blocksManager.getBlockEditing().selectedBlockType ===
                "divider" && (
                <DividerOptions
                  blockId={blocksManager.getBlockEditing().selectedBlockId!}
                />
              )}
              {blocksManager.getBlockEditing().selectedBlockType ===
                "heading" && (
                <HeadingStyles
                  blockId={blocksManager.getBlockEditing().selectedBlockId!}
                />
              )}
              {blocksManager.getBlockEditing().selectedBlockType === "text" && (
                <TextStyles
                  blockId={blocksManager.getBlockEditing().selectedBlockId!}
                />
              )}
              {blocksManager.getBlockEditing().selectedBlockType === "image" && (
                <ImageOptions
                  blockId={blocksManager.getBlockEditing().selectedBlockId!}
                />
              )}
              {blocksManager.getBlockEditing().selectedBlockType ===
                "hero_slider" && <HeroSliderOptions />
              }
              {blocksManager.getBlockEditing().selectedBlockType === "video" && 
                <VideoOptions blockId={blocksManager.getBlockEditing().selectedBlockId!} />
              }
              {blocksManager.getBlockEditing().selectedBlockType === "socials" && 
                <SocialsOptions blockId={blocksManager.getBlockEditing().selectedBlockId!} />
              }
              {blocksManager.getBlockEditing().selectedBlockType === "html" && 
                <CustomHtmlOptions blockId={blocksManager.getBlockEditing().selectedBlockId!} />
              }
            </div>
          )}
      </div>
    </div>
  );
});
